import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import React, { ReactElement, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import menuIcon from "../assets/icons/menu-icon.svg";
import { useAuth } from "../context/AuthContext";
import useTestData from "../hooks/useTestData";
import { ROUTES } from "../routes/config";
import Link from "./Link";
import Loader from "./AppLoader";
import Logo from "./Logo";
import DataInputIcon from "./icons/DataInputIcon";
import DocumentIcon from "./icons/DocumentIcon";
import InterpretationIcon from "./icons/InterpretationIcon";
import OpenTestIcon from "./icons/OpenTestIcon";
import useDisclosure from "../hooks/useDisclouser";
import AboutModal from "./AboutModal/AboutModal";
import HelpModal from "./HelpModal";
import RawDataModal from "./RawDataModal";
import { USER_ROLES } from "../types";
import { twMerge } from "tailwind-merge";

export default function Navbar() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    const { value: testData, error: testError } = useTestData();
    const location = useLocation(); // refetch navigation on location change
    const { testId } = testData ?? {};

    const noTest = Boolean(!testData || testError);

    const testPathPrefix = `test/${testId}/`;
    const navbarStatus = testData?.navbarStatus;
    const isAnaerobicPath = location.pathname.includes(
        ROUTES.INTERPRETATION.subroutes.ANAEROBIC_THRESHOLD.path
    );

    const {
        isOpen: isAboutDialogOpen,
        onClose: closeAboutDialog,
        onOpen: openAboutDialog,
    } = useDisclosure();
    const {
        isOpen: isHelpModalOpen,
        onClose: closeHelpModal,
        onOpen: openHelpModal,
    } = useDisclosure();
    const {
        isOpen: isRawDataModalOpen,
        onClose: closeRawDataModal,
        onOpen: openRawDataModal,
    } = useDisclosure();

    const navigation = [
        {
            ...ROUTES.OPEN_TEST,
            icon: <OpenTestIcon />,
        },
        {
            ...ROUTES.DATA_INPUT,
            path: testPathPrefix + ROUTES.DATA_INPUT.path,
            icon: <DataInputIcon />,
            disabled: noTest,
        },
        {
            ...ROUTES.INTERPRETATION,
            subroutes:
                testData?.testStatus === "Not completed" && isAnaerobicPath
                    ? undefined
                    : Object.values(ROUTES.INTERPRETATION.subroutes).map(
                          ({ path, label }) => ({
                              label,
                              path: `/${testPathPrefix}${ROUTES.INTERPRETATION.path}/${path}`,
                              active: location.pathname.includes(path),
                          })
                      ),
            path:
                testData?.testStatus === "Not completed" && isAnaerobicPath
                    ? `/${testPathPrefix}${ROUTES.INTERPRETATION.path}/${ROUTES.INTERPRETATION.subroutes.SUMMARY_TABLE.path}`
                    : `/${testPathPrefix}${ROUTES.INTERPRETATION.path}`,
            icon: <InterpretationIcon />,
            disabled: noTest || !navbarStatus || navbarStatus < 1,
        },
        {
            ...ROUTES.REPORT,
            path: testPathPrefix + ROUTES.REPORT.path,
            icon: <DocumentIcon />,
            disabled: noTest || Boolean(navbarStatus ? navbarStatus < 2 : true),
        },
    ];

    const menuItems = [
        { title: "Help", onClick: openHelpModal },
        ...(testId ? [{ title: "Raw data", onClick: openRawDataModal }] : []),
        { title: "About version", onClick: openAboutDialog },
    ];

    return (
        <nav className="flex justify-between bg-white h-[53px] xl:h-20 shadow-[0px_4px_4px_0px_#00000040] ps-8 pe-6">
            <div className="flex gap-8 xl:gap-[115px]">
                <Logo className="my-auto object-contain" />
                {user?.role !== USER_ROLES.LOCAL_ADMIN && (
                    <ul className="flex">
                        {navigation.map((route) => {
                            const isCurrentRoute =
                                window.location.pathname.includes(route.path);
                            return (
                                <li key={route.path}>
                                    <NavItem
                                        {...route}
                                        active={isCurrentRoute}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
            <div className="flex items-center gap-2.5 xl:gap-4">
                {loading ? (
                    <Loader />
                ) : user ? (
                    <span className="text-sm xl:text-base">
                        {user.userName}
                    </span>
                ) : null}
                <div className="flex gap-0.5 xl:gap-1.5 items-center">
                    <img
                        src={user?.avatar}
                        alt="User Avatar"
                        className="size-8 xl:size-10 rounded-full"
                    />
                    <Menu>
                        <MenuButton>
                            <ChevronDownIcon className="p-1 w-6 h-6 stroke-blue-dark" />
                        </MenuButton>
                        <MenuItems
                            anchor="bottom end"
                            className="w-[180px] mt-5 shadow-[0px_4px_4px_0px_#00000040] bg-white py-1.5 px-3 rounded-b-[4px]"
                        >
                            <MenuItem>
                                <button
                                    className="h-9 px-3 font-semibold text-blue-dark"
                                    onClick={() => {
                                        navigate(`/${ROUTES.LOGOUT.path}`);
                                    }}
                                >
                                    Logout
                                </button>
                            </MenuItem>
                        </MenuItems>
                    </Menu>
                </div>
                <Menu>
                    <MenuButton>
                        <div className="aspect-square p-2 rounded-full flex items-center justify-center hover:bg-blue-dark/10">
                            <img
                                src={menuIcon}
                                alt="3 horizontal bars"
                                className="w-5 xl:w-6"
                            />
                        </div>
                    </MenuButton>
                    <MenuItems
                        anchor="bottom end"
                        className="border border-[#D9D9D9] flex flex-col gap-3 w-[260px] mt-2 xl:mt-5 shadow-[0px_4px_4px_0px_#00000040] bg-white py-1.5 px-3 rounded-b-[4px]"
                    >
                        {menuItems.map((item) => (
                            <MenuItem key={item.title}>
                                <button
                                    onClick={item?.onClick}
                                    className="text-start h-9 px-3 font-semibold text-blue-dark hover:bg-blue-option-select"
                                >
                                    {item.title}
                                </button>
                            </MenuItem>
                        ))}
                    </MenuItems>
                </Menu>
            </div>
            <AboutModal
                isOpen={isAboutDialogOpen}
                onCancel={closeAboutDialog}
            />
            <HelpModal isOpen={isHelpModalOpen} onClose={closeHelpModal} />
            <RawDataModal
                isOpen={isRawDataModalOpen}
                onCancel={closeRawDataModal}
            />
        </nav>
    );
}

type NavItemProps = {
    path: string;
    label: string;
    icon: React.ReactNode;
    active: boolean;
    disabled?: boolean;
    subroutes?: { path: string; label: string; active: boolean }[];
};

const NavItem = ({
    path,
    label,
    icon,
    active,
    disabled,
    subroutes,
}: NavItemProps) => {
    const [showSubroutes, setShowSubroutes] = useState(false);
    const navigate = useNavigate();

    const iconWithColor = React.Children.map(icon, (child) => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child as ReactElement, {
                className:
                    "shrink-0 max-xl:w-6 " +
                    (active
                        ? "stroke-blue-dark"
                        : disabled
                        ? "stroke-gray-disabled"
                        : "stroke-blue-medium"),
            });
        }
        return child;
    });

    useEffect(() => {
        // Close subroutes when clicking outside
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest(".Subroutes")) {
                setShowSubroutes(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    return (
        <div className="relative h-full text-blue-medium font-semibold">
            <Link
                to={path.startsWith("/") ? path : `/${path}`}
                disabled={disabled}
                onClick={() => setShowSubroutes(false)}
                className={twMerge(
                    `h-full flex items-center px-3.5 xl:px-6 py-2 gap-3 hover:bg-blue-light`,
                    active && "text-blue-dark font-bold hover:bg-blue-light",
                    disabled &&
                        "text-gray-disabled font-semibold cursor-not-allowed"
                )}
            >
                <div
                    className={`h-full w-full flex items-center gap-2 xl:gap-3`}
                >
                    {iconWithColor}
                    <span className="text-sm sm:text-base xl:text-xl">
                        {label}
                    </span>
                </div>
                {subroutes ? (
                    <div className="Subroutes group">
                        <ChevronDownIcon
                            onClick={(ev) => {
                                ev.stopPropagation();
                                ev.preventDefault();
                                setShowSubroutes((prev) => !prev);
                            }}
                            className={twMerge(
                                "w-9 h-9 stroke-blue-dark cursor-pointer rounded-full p-2 hover:bg-blue-dark/10",
                                disabled &&
                                    "pointer-events-none stroke-gray-disabled"
                            )}
                        />
                    </div>
                ) : null}
                <div
                    className={`absolute bottom-0 left-0 w-full h-1 ${
                        active ? "bg-blue-dark" : ""
                    }`}
                />
            </Link>
            {Boolean(subroutes) && (
                <div
                    className={`z-20 ${
                        showSubroutes ? "scale-100" : "scale-0"
                    } shadow-[0px_4px_4px_0px_#00000040] font-semibold bg-white flex flex-col gap-1 xl:gap-3 absolute top-full left-0 z-10 w-full rounded-b-[4px] py-1.5`}
                >
                    {Object.values(subroutes!).map(
                        ({ path, label, active }) => (
                            <Link
                                to={path}
                                key={path}
                                onClick={() => {
                                    const isInbar12NavItem = path.includes(
                                        ROUTES.INTERPRETATION.subroutes.INBAR12
                                            .path
                                    );
                                    const isInbar12ActivePath =
                                        window.location.pathname.includes(
                                            ROUTES.INTERPRETATION.subroutes
                                                .INBAR12.path
                                        );

                                    const isWasserman9NavItem = path.includes(
                                        ROUTES.INTERPRETATION.subroutes
                                            .WASSWERMAN9.path
                                    );
                                    const isWasserman9ActivePath =
                                        window.location.pathname.includes(
                                            ROUTES.INTERPRETATION.subroutes
                                                .WASSWERMAN9.path
                                        );
                                    if (
                                        (isInbar12NavItem &&
                                            isInbar12ActivePath) ||
                                        (isWasserman9NavItem &&
                                            isWasserman9ActivePath)
                                    ) {
                                        window.location.reload();
                                        navigate(path);
                                    }

                                    setShowSubroutes(false);
                                }}
                                className={`block hover:bg-blue-option-select px-3 py-1 text-sm xl:text-base ${
                                    active
                                        ? "bg-blue-option-select"
                                        : "bg-white"
                                }`}
                            >
                                {label}
                            </Link>
                        )
                    )}
                </div>
            )}
        </div>
    );
};
